import { apiClient } from "./apiClient";
import { logger } from "./logger";

export interface ShadowModeUpdateResponse {
  status: string;
  shadow_mode_enabled: boolean;
}

export interface AiEngineDecision {
  id: string;
  pair: string;
  decision: string;
  confidence: number;
  estimated_gain_pct: number;
  was_shadow: boolean;
  actual_result: string | null;
  actual_gain_pct: number | null;
  reasoning: string;
  created_at: string;
  direction?: "long" | "short";
  tokens_used?: number;
  cost?: number;
  latency_ms?: number;
  snapshot_data?: Record<string, unknown>;
}

export interface EngineStats {
  mode: "rule_based" | "ai_technical" | "ai_orderbook";
  trades_analyzed_today: number;
  trades_accepted_today: number;
  trades_rejected_today: number;
  accept_rate: number;
  avg_ai_confidence: number;
  win_rate: number;
  prediction_accuracy: number;
  target_prediction_accuracy: number;
  daily_api_calls: number;
  max_daily_api_calls: number;
  daily_cost: number;
  max_daily_cost: number;
  llm_api_status: "connected" | "error" | "quota_exceeded";
  shadow_mode: boolean;
  custom_instructions_active: boolean;
}

export interface DecisionDetail {
  id: string;
  pair: string;
  decision: string;
  direction: string;
  confidence: number;
  estimated_gain_pct: number;
  reasoning: string;
  full_reasoning: string;
  created_at: string;
  was_shadow: boolean;
  actual_result: string | null;
  actual_gain_pct: number | null;
  tokens_used: number;
  cost: number;
  latency_ms: number;
  snapshot_data: Record<string, unknown> | null;
}

export interface ShadowStats {
  total_analyzed: number;
  accepted: number;
  rejected: number;
  theoretical_win_rate: number;
  win_rate_calculation_basis: number;
  comparison_win_rate: number;
  theoretical_pnl_pct: number;
  estimated_profit: number;
  estimated_loss: number;
  total_accuracy: number;
  accuracy_basis: number;
  shadow_days_active: number;
  total_trades: number;
  last_updated: string;
}

export interface ShadowReadiness {
  ready: boolean;
  conditions: {
    time: { met: boolean; current_days: number; required_days: number; progress_pct: number };
    trades: { met: boolean; current_trades: number; required_trades: number; progress_pct: number };
    win_rate: { met: boolean; current_win_rate: number; required_win_rate: number; progress_pct: number };
  };
  overall_progress_pct: number;
}

export interface ComparisonEntry {
  pair: string;
  ai_decision: string;
  ai_confidence: number;
  ai_estimated_gain: number;
  rule_based_result: string;
  rule_based_gain_pct: number;
  verdict: string;
  conflict_type: string | null;
  ai_was_correct: boolean;
  timestamp: string;
}

export async function updateShadowMode(
  shadowModeEnabled: boolean,
  reason?: string,
): Promise<ShadowModeUpdateResponse> {
  try {
    const response = await apiClient.put<ShadowModeUpdateResponse>(
      "/admin/ai-engine/shadow-mode",
      { shadow_mode_enabled: shadowModeEnabled, reason },
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error updating shadow mode:",
      error instanceof Error ? error : new Error(String(error)),
    );
    throw error;
  }
}

export async function getAiEngineDecisions(): Promise<AiEngineDecision[]> {
  try {
    const response = await apiClient.get<AiEngineDecision[]>(
      "/admin/ai-engine/decisions",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching AI engine decisions:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return [];
  }
}

export async function getShadowStats(): Promise<ShadowStats> {
  try {
    const response = await apiClient.get<ShadowStats>(
      "/admin/ai-engine/shadow-stats",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching shadow stats:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return {
      total_analyzed: 0,
      accepted: 0,
      rejected: 0,
      theoretical_win_rate: 0,
      win_rate_calculation_basis: 0,
      comparison_win_rate: 0,
      theoretical_pnl_pct: 0,
      estimated_profit: 0,
      estimated_loss: 0,
      total_accuracy: 0,
      accuracy_basis: 0,
      shadow_days_active: 0,
      total_trades: 0,
      last_updated: new Date().toISOString(),
    };
  }
}

export async function getShadowReadiness(): Promise<ShadowReadiness> {
  try {
    const response = await apiClient.get<ShadowReadiness>(
      "/admin/ai-engine/shadow-readiness",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching shadow readiness:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return {
      ready: false,
      conditions: {
        time: { met: false, current_days: 0, required_days: 7, progress_pct: 0 },
        trades: { met: false, current_trades: 0, required_trades: 50, progress_pct: 0 },
        win_rate: { met: false, current_win_rate: 0, required_win_rate: 60, progress_pct: 0 },
      },
      overall_progress_pct: 0,
    };
  }
}

export async function getShadowComparison(): Promise<ComparisonEntry[]> {
  try {
    const response = await apiClient.get<ComparisonEntry[]>(
      "/admin/ai-engine/shadow-comparison",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching shadow comparison:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return [];
  }
}

export async function getEngineStats(): Promise<EngineStats | null> {
  try {
    const response = await apiClient.get<EngineStats>(
      "/admin/ai-engine/stats",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching engine stats:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return null;
  }
}

export interface LearningLedgerEntry {
  pair: string;
  result: string;
  prediction_accuracy: number;
  gain: number;
  analysis: string;
}

export async function getLearningLedger(): Promise<LearningLedgerEntry[]> {
  try {
    const response = await apiClient.get<LearningLedgerEntry[]>(
      "/admin/ai-engine/learning-log",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching learning ledger:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return [];
  }
}

export interface PromptRule {
  rule_text: string;
  type: string;
  source: string;
  active: boolean;
}

export async function getPromptRules(): Promise<PromptRule[]> {
  try {
    const response = await apiClient.get<PromptRule[]>(
      "/admin/ai-engine/prompt-rules",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching prompt rules:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return [];
  }
}

export interface ReevaluationTrigger {
  trigger_type: string;
  count: number;
}

export async function getReevaluationTriggers(): Promise<ReevaluationTrigger[]> {
  try {
    const response = await apiClient.get<ReevaluationTrigger[]>(
      "/admin/ai-engine/reeval-stats",
    );
    const data = response.data;
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === "object") {
      return Object.entries(data).map(([trigger_type, count]) => ({
        trigger_type,
        count: typeof count === "number" ? count : 0,
      }));
    }
    return [];
  } catch (error) {
    logger.error(
      "Error fetching reevaluation triggers:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return [];
  }
}

export interface ConflictRecord {
  id: string;
  pair: string;
  ai_decision: string;
  rule_based_decision: string;
  conflict_type: string;
  resolved: boolean;
  created_at: string;
}

export interface DiscoveredPair {
  id: string;
  pair: string;
  direction: string;
  current_price: number | null;
  decision: string;
  confidence: number;
  reasoning: string | null;
  created_at: string | null;
}

export async function getDiscoveredPairs(): Promise<DiscoveredPair[]> {
  try {
    const response = await apiClient.get<DiscoveredPair[]>(
      "/admin/ai-engine/discovered-pairs",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching discovered pairs:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return [];
  }
}

export async function getConflictRecords(): Promise<ConflictRecord[]> {
  try {
    const response = await apiClient.get<ConflictRecord[]>(
      "/admin/ai-engine/conflicts",
    );
    return response.data;
  } catch (error) {
    logger.error(
      "Error fetching conflict records:",
      error instanceof Error ? error : new Error(String(error)),
    );
    return [];
  }
}
